import * as Sentry from '@sentry/react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';

import { ROUTES } from '@/config/routes';
import { useSignIn, useSignOut, useInsertLoginLog } from '@/hooks/queries';
import { supabase } from '@/integrations/supabase/client';

import { AuthConfig, UseAuthReturn } from '../types';

const identifierSchema = z
  .string()
  .min(3, "L'identifiant doit contenir au moins 3 caractères")
  .max(255, "L'identifiant est trop long");

/**
 * Authentication Hook
 *
 * Handles user login with email/password.
 * SIGNUP DISABLED - Only login functionality is available.
 *
 * @param config - Authentication configuration from auth-config.json
 * @returns Authentication state and handlers
 */
export const useAuth = (config: AuthConfig): UseAuthReturn => {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');

  const signInMutation = useSignIn();
  const signOutMutation = useSignOut();
  const insertLoginLogMutation = useInsertLoginLog();

  const loading = signInMutation.isPending || signOutMutation.isPending;

  /**
   * Résout un identifier (email ou username) en email pour Supabase Auth
   */
  const resolveIdentifierToEmail = async (input: string): Promise<string | null> => {
    const trimmedInput = input.trim();

    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'Resolving identifier to email',
      level: 'info',
      data: { isEmail: trimmedInput.includes('@') },
    });

    // Si l'input contient "@", c'est un email
    if (trimmedInput.includes('@')) {
      return trimmedInput;
    }

    // Sinon, chercher le username dans profiles
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('email')
        .ilike('username', trimmedInput)
        .maybeSingle();

      if (error) {
        Sentry.captureException(error, {
          tags: { component: 'useAuth', operation: 'resolveUsername' },
        });
        return null;
      }

      if (!data || !data.email) {
        Sentry.addBreadcrumb({
          category: 'auth',
          message: 'Username not found',
          level: 'warning',
        });
        return null;
      }

      return data.email;
    } catch (err) {
      Sentry.captureException(err, {
        tags: { component: 'useAuth', operation: 'resolveUsername' },
      });
      return null;
    }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();

    await Sentry.startSpan({ name: 'useAuth.handleAuth', op: 'auth.login' }, async () => {
      try {
        // Validation de l'identifier
        const validationResult = identifierSchema.safeParse(identifier);
        if (!validationResult.success) {
          throw new Error(validationResult.error.errors[0].message);
        }

        Sentry.addBreadcrumb({
          category: 'auth',
          message: `Login attempt with identifier: ${identifier}`,
          level: 'info',
        });

        // Résoudre l'identifier en email
        const email = await resolveIdentifierToEmail(identifier);

        if (!email) {
          throw new Error('Username not found. Please check your credentials.');
        }

        Sentry.addBreadcrumb({
          category: 'auth',
          message: 'Identifier resolved to email',
          level: 'info',
        });

        // Authentification avec Supabase via React Query
        const data = await signInMutation.mutateAsync({
          email,
          password,
        });

        // Log the login event via React Query
        if (data.user) {
          await insertLoginLogMutation.mutateAsync({
            userId: data.user.id,
          });
        }

        toast.success(config.messages.loginSuccess);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'An error occurred';

        Sentry.captureException(error, {
          tags: {
            component: 'useAuth',
            operation: 'login',
            identifier,
          },
        });

        toast.error(errorMessage);
      }
    });
  };

  const handleSignOut = async () => {
    try {
      await signOutMutation.mutateAsync();

      toast.success('Successfully logged out');
      navigate(ROUTES.AUTH);
    } catch (error) {
      console.error('Error signing out:', error);
      Sentry.captureException(error, {
        tags: { component: 'useAuth', operation: 'signOut' },
      });
      toast.error('An error occurred during logout');
    }
  };

  return {
    email: identifier,
    setEmail: setIdentifier,
    password,
    setPassword,
    loading,
    handleAuth,
    handleSignOut,
  };
};
